import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { maskCurrency, digitsToAmount } from '../../lib/format'
import type { Goal } from '../../types'
import { getToastErrorMessage, useToast } from '../Toast'

interface AddGoalSheetProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: { name: string; target_amount: number; emoji?: string | null; deadline?: string | null }) => Promise<void>
  editing?: Goal | null
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0]
}

export default function AddGoalSheet({ open, onClose, onSubmit, editing }: AddGoalSheetProps) {
  const toast = useToast()
  const [name,          setName]          = useState('')
  const [amountDigits,  setAmountDigits]  = useState('')
  const [emoji,         setEmoji]         = useState('')
  const [deadline,      setDeadline]      = useState('')
  const [submitting,    setSubmitting]    = useState(false)
  const [error,         setError]         = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setName(editing.name)
      setAmountDigits(Math.round(editing.target_amount * 100).toString())
      setEmoji(editing.emoji ?? '')
      setDeadline(editing.deadline ?? '')
    } else {
      setName('')
      setAmountDigits('')
      setEmoji('')
      setDeadline('')
    }
    setError(null)
  }, [open, editing])

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Nome é obrigatório')
      toast.warning('Informe o nome da meta.')
      return
    }
    const amount = digitsToAmount(amountDigits)
    if (amount <= 0) {
      setError('Informe um valor alvo')
      toast.warning('Informe um valor alvo.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        name: trimmedName,
        target_amount: amount,
        emoji: emoji.trim() || null,
        deadline: deadline || null,
      })
      onClose()
    } catch (err) {
      setError(getToastErrorMessage(err, 'Erro ao salvar meta.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bc-sheet-overlay" onClick={onClose}>
      <div className="bc-sheet" onClick={e => e.stopPropagation()}>
        <div className="bc-sheet__header">
          <span className="bc-sheet__title">{editing ? 'Editar meta' : 'Nova meta'}</span>
          <button className="bc-sheet__close" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        <form className="bc-sheet__body" onSubmit={handleSubmit}>
          <div className="bc-field">
            <label className="bc-label">Nome da meta</label>
            <input
              className="bc-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Guitarra nova"
              maxLength={80}
              autoFocus
            />
          </div>

          <div className="bc-field">
            <label className="bc-label">Valor alvo</label>
            <div className="bc-currency">
              <span className="bc-currency__prefix">R$</span>
              <input
                className="bc-input"
                type="text"
                inputMode="numeric"
                value={maskCurrency(amountDigits)}
                onChange={e => setAmountDigits(e.target.value.replace(/\D/g, ''))}
                placeholder="0,00"
              />
            </div>
          </div>

          <div className="bc-field">
            <label className="bc-label">
              Emoji <span className="bc-label__opt">(opcional)</span>
            </label>
            <input
              className="bc-input"
              type="text"
              value={emoji}
              onChange={e => setEmoji(e.target.value)}
              placeholder="🎸"
              maxLength={4}
            />
          </div>

          <div className="bc-field">
            <label className="bc-label">
              Prazo <span className="bc-label__opt">(opcional)</span>
            </label>
            <input
              className="bc-input"
              type="date"
              value={deadline}
              min={todayIso()}
              onChange={e => setDeadline(e.target.value)}
            />
          </div>

          {error && <p className="bc-field__error">{error}</p>}

          <div className="bc-sheet__footer">
            <button type="button" className="bc-btn bc-btn--ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="bc-btn bc-btn--primary" disabled={submitting}>
              {submitting ? 'Salvando…' : editing ? 'Salvar' : 'Criar meta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
